// Nick field glue (ui-flow §4.2, §14.1; protocol §9.2). DOM-free, so it is unit-tested.
// - While typing: control, zero-width and bidi-control characters are dropped and the value is
//   cut to 15 code points (`cleanNickInput`). Whitespace is left alone so spaces can be typed.
// - On Play: the full `sanitizeNick` (NFC, collapse whitespace, trim, 15 code points) gives the
//   nick that is sent and compared ("New name applies next round").
// - `ballio.nick` is saved on every change, debounced by 300 ms (`NickPersistence`).

import {
  codePointLength,
  isStrippedCodePoint,
  NICK_MAX_CODE_POINTS,
  sanitizeNick,
  truncateCodePoints,
} from '@ballio/shared';
import { readKey, STORAGE_KEYS, type StorageLike, writeKey } from '../../settings/store.js';

export const NICK_SAVE_DEBOUNCE_MS = 300;

/** The field value after one edit: stripped characters removed, cut to the code-point limit. */
export function cleanNickInput(
  value: string,
  maxCodePoints: number = NICK_MAX_CODE_POINTS,
): string {
  let out = '';
  for (const ch of value) {
    if (!isStrippedCodePoint(ch.codePointAt(0) ?? 0)) out += ch;
  }
  return codePointLength(out) > maxCodePoints ? truncateCodePoints(out, maxCodePoints) : out;
}

/** The nick to send and display for a field value (protocol §9.2 steps 1–4). */
export function finalNick(value: string): string {
  return sanitizeNick(value);
}

/** True when the field's nick differs from the current life's (ui-flow §5 note). */
export function nickChanged(fieldValue: string, lifeNick: string | null): boolean {
  return lifeNick !== null && finalNick(fieldValue) !== lifeNick;
}

export interface TimerApi {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

const GLOBAL_TIMERS: TimerApi = {
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
};

/** Loads and (debounced) saves `ballio.nick`. Storage failures are silent (ui-flow §14.1). */
export class NickPersistence {
  private readonly storage: StorageLike | null | undefined;
  private readonly delayMs: number;
  private readonly timers: TimerApi;
  private pending: string | null = null;
  private handle: unknown = null;

  /** `storage` undefined = the browser's localStorage; null = no storage (memory only). */
  constructor(
    storage?: StorageLike | null,
    delayMs: number = NICK_SAVE_DEBOUNCE_MS,
    timers: TimerApi = GLOBAL_TIMERS,
  ) {
    this.storage = storage;
    this.delayMs = delayMs;
    this.timers = timers;
  }

  /** The stored nick, cleaned like typed input (≤ 15 code points); "" when none. */
  load(): string {
    // An undefined storage falls through to readKey's default (the browser's localStorage).
    const raw = readKey(STORAGE_KEYS.nick, this.storage);
    return raw === null ? '' : cleanNickInput(raw);
  }

  /** The field changed: save `value` after the debounce (restarting it on every change). */
  change(value: string): void {
    this.pending = value;
    if (this.handle !== null) this.timers.clearTimeout(this.handle);
    this.handle = this.timers.setTimeout(() => {
      this.handle = null;
      this.flush();
    }, this.delayMs);
  }

  /** Saves a pending change now (Play, page hide). */
  flush(): void {
    if (this.handle !== null) {
      this.timers.clearTimeout(this.handle);
      this.handle = null;
    }
    if (this.pending === null) return;
    const value = this.pending;
    this.pending = null;
    writeKey(STORAGE_KEYS.nick, value, this.storage);
  }

  /** Drops a pending change without saving it. */
  cancel(): void {
    if (this.handle !== null) this.timers.clearTimeout(this.handle);
    this.handle = null;
    this.pending = null;
  }
}
